// src/pages/LocationPickerPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Standalone map page used by "Add" / "Update" actions (e.g. from
// OnStreetMasterData). Sirf map dikhata hai — same look/style jo
// Off-Street/On-Street map mein hai — jahan user click karke ya pin drag
// karke naya lat/long select kar sakta hai (Add mode) ya kisi existing
// device ki location update kar sakta hai (Update mode). Ek optional "zone
// boundary" polygon bhi manually (vertex-by-vertex click) draw kar sakte ho.
//
// NOTE: Google ne `google.maps.drawing.DrawingManager` ko Maps JavaScript API
// v3.65+ mein deprecate/remove kar diya hai, isliye polygon drawing yahan
// manually (apne khud ke click handling se) implement ki gayi hai — koi extra
// "drawing" library load karne ki zaroorat nahi.
//
// Usage (navigate se):
//   Add:    navigate(`/location-picker?mode=add`)
//   Update: navigate(`/location-picker?mode=update&pnd_number=123&lat=24.71&lng=46.67`)
//   Existing boundary bhi pass kar sakte ho: &boundary=[{"lat":24.71,"lng":46.67},...]
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ChevronLeft,
  MapPin,
  Loader2,
  Check,
  X,
  Shapes,
  Trash2,
  PenLine,
  Undo2,
  CheckCircle2,
  Layers,
} from "lucide-react";
import { GOOGLE_MAP_KEY } from "@/constants/constant";
import { useTheme } from "@/components/theme-provider";
import { apiCall } from "@/api/apiCall";

// ─── Same map styles as Off-Street/On-Street map ──────────────────────────────
const getDarkMapStyles = () => [
  { elementType: "geometry", stylers: [{ color: "#1a1d28" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1a1d28" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8b92a8" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#d0d3de" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#6b7280" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#232938" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#2c3142" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0f1419" }] },
];

const getLightMapStyles = () => [
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#e5e7eb" }] },
  { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#d1d5db" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#dbeafe" }] },
];

const DEFAULT_CENTER = { lat: 24.0, lng: 45.0 };
const DEFAULT_ZOOM = 6;
const PIN_ZOOM = 16;
const BOUNDARY_COLOR = "#3B82F6";

// ─── "Existing locations" reference layer (all/nearby project & street data
// pulled from the same /cp-map/pnd-street/all API used on OnStreetMasterData) ──
const EXISTING_ACTIVE_COLOR = "#22C55E"; // green dot — device_status ACTIVE
const EXISTING_INACTIVE_COLOR = "#EF4444"; // red dot — device_status INACTIVE
const GROUP_OUTLINE_COLOR = "#F59E0B"; // amber — street group hull/outline
const EXISTING_PAGE_SIZE = 500;
const EXISTING_MAX_PAGES = 20; // safety cap (~10k records) so a huge dataset can't hang the map

// Simple monotone-chain convex hull over {lat,lng} points (treats lng as x,
// lat as y — fine for the small, local areas a single street/project spans).
// Returns null if there aren't at least 3 distinct points to form a shape.
const convexHull = (points) => {
  const uniq = [];
  const seen = new Set();
  points.forEach((p) => {
    const key = `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniq.push({ x: p.lng, y: p.lat });
    }
  });
  if (uniq.length < 3) return null;

  uniq.sort((a, b) => a.x - b.x || a.y - b.y);
  const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

  const lower = [];
  for (const p of uniq) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper = [];
  for (let i = uniq.length - 1; i >= 0; i -= 1) {
    const p = uniq[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  const hull = lower.concat(upper);
  if (hull.length < 3) return null;
  return hull.map((p) => ({ lat: p.y, lng: p.x }));
};

const buildPinIcon = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 64 64">
    <path d="M32 3C20 3 10 13 10 25c0 18 22 36 22 36s22-18 22-36C54 13 44 3 32 3z" fill="#3B82F6"/>
    <circle cx="32" cy="25" r="11" fill="white"/>
    <circle cx="32" cy="25" r="5" fill="#3B82F6"/>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(46, 46),
    anchor: new window.google.maps.Point(23, 46),
  };
};

const LocationPickerPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme: appTheme } = useTheme();
  const [isDarkMode, setIsDarkMode] = useState(appTheme === "dark");
  useEffect(() => setIsDarkMode(appTheme === "dark"), [appTheme]);

  const mode = searchParams.get("mode") === "update" ? "update" : "add";
  const pndNumber = searchParams.get("pnd_number") || "";
  const initialLat = parseFloat(searchParams.get("lat"));
  const initialLng = parseFloat(searchParams.get("lng"));
  const hasInitialPosition = !isNaN(initialLat) && !isNaN(initialLng);

  // Optional project/street context (passed from OnStreetMasterData row
  // actions) used to filter which existing points show as a reference layer.
  const contextParkCd = searchParams.get("park_cd") || "";
  const contextCityCd = searchParams.get("city_cd") || "";
  const contextStreetCd = searchParams.get("street_cd") || "";

  // Existing boundary (update mode) can be passed as a JSON-encoded array of
  // {lat,lng} points via ?boundary=[...]
  const initialBoundary = (() => {
    const raw = searchParams.get("boundary");
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length >= 3 ? parsed : null;
    } catch {
      return null;
    }
  })();

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const polygonRef = useRef(null); // finalized, editable boundary polygon
  const tempPolygonRef = useRef(null); // in-progress preview while drawing
  const vertexMarkersRef = useRef([]); // little dots at each clicked point
  const isDrawingBoundaryRef = useRef(false);
  const drawingPointsRef = useRef([]);
  const existingMarkersRef = useRef([]); // reference dots for other existing records
  const existingOverlaysRef = useRef([]); // reference street-group hull polygons/lines
  const existingInfoWindowRef = useRef(null);
  const hasAutoFitRef = useRef(false); // only auto-fit the viewport once, on first data load

  const [mapReady, setMapReady] = useState(false);
  const [mapLoadError, setMapLoadError] = useState(false);
  const [position, setPosition] = useState(
    hasInitialPosition ? { lat: initialLat, lng: initialLng } : null
  );
  const [boundaryPath, setBoundaryPath] = useState(initialBoundary);
  const [isDrawingBoundary, setIsDrawingBoundary] = useState(false);
  const [drawingPointCount, setDrawingPointCount] = useState(0);
  const [saving, setSaving] = useState(false);

  // ── Existing locations reference layer ──────────────────────────────────────
  const [existingRecords, setExistingRecords] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [showExistingLayer, setShowExistingLayer] = useState(true);

  // ── Load Google Maps script ─────────────────────────────────────────────────
  // Just the core + geometry lib — polygon drawing here is done manually, so no
  // extra "drawing" library is needed (it's deprecated in the Maps JS API anyway).
  useEffect(() => {
    if (window.google?.maps) {
      setMapReady(true);
      return;
    }
    if (!document.getElementById("google-maps-script")) {
      const script = document.createElement("script");
      script.id = "google-maps-script";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_KEY}&libraries=geometry`;
      script.async = true;
      script.defer = true;
      script.onload = () => setMapReady(true);
      script.onerror = () => setMapLoadError(true);
      document.head.appendChild(script);
    } else {
      let attempts = 0;
      const check = setInterval(() => {
        attempts += 1;
        if (window.google?.maps) {
          setMapReady(true);
          clearInterval(check);
        } else if (attempts > 100) {
          setMapLoadError(true);
          clearInterval(check);
        }
      }, 100);
      return () => clearInterval(check);
    }
  }, []);

  // ── Load existing records (same API OnStreetMasterData uses) ───────────────
  // Runs once on mount, independent of the Google Maps script, so the data is
  // ready as soon as the map itself is ready.
  useEffect(() => {
    let cancelled = false;

    const fetchAllExistingRecords = async () => {
      try {
        setLoadingExisting(true);
        let all = [];
        let page = 1;
        let totalPages = 1;

        do {
          const res = await apiCall(
            "GET",
            `/cp-map/pnd-street/all?page=${page}&limit=${EXISTING_PAGE_SIZE}`,
            null,
            navigate
          );
          if (!res?.success) break;
          all = all.concat(res.data || []);
          totalPages = res.totalPages || 1;
          page += 1;
        } while (page <= totalPages && page <= EXISTING_MAX_PAGES);

        if (!cancelled) setExistingRecords(all);
      } catch (err) {
        console.error("Failed to load existing locations:", err);
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    };

    fetchAllExistingRecords();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter down to valid, relevant points: same project/street context (if
  // passed in via URL) and excluding the record currently being edited so it
  // doesn't show as a duplicate of the active draggable pin.
  const filteredExisting = useMemo(() => {
    let list = existingRecords;

    if (contextParkCd) list = list.filter((r) => String(r.park_cd ?? "") === String(contextParkCd));
    else if (contextCityCd) list = list.filter((r) => String(r.city_cd ?? "") === String(contextCityCd));

    if (mode === "update" && pndNumber) {
      list = list.filter((r) => String(r.pnd_number ?? "") !== String(pndNumber));
    }

    return list.filter((r) => {
      const lat = parseFloat(r.latitude);
      const lng = parseFloat(r.longitude);
      return !isNaN(lat) && !isNaN(lng);
    });
  }, [existingRecords, contextParkCd, contextCityCd, mode, pndNumber]);

  // Group filtered points by street so a hull/outline can be drawn per street.
  const existingGroups = useMemo(() => {
    const groups = {};
    filteredExisting.forEach((r) => {
      const key = r.street_cd || r.street_name || "unknown";
      if (!groups[key]) groups[key] = { key, streetName: r.street_name || r.street_cd || "Unknown street", points: [] };
      groups[key].points.push({
        ...r,
        lat: parseFloat(r.latitude),
        lng: parseFloat(r.longitude),
      });
    });
    return Object.values(groups);
  }, [filteredExisting]);

  // ── Device pin marker ───────────────────────────────────────────────────────
  const placeMarker = useCallback((latLng) => {
    const map = mapInstanceRef.current;
    if (!map || !window.google) return;
    if (markerRef.current) {
      markerRef.current.setPosition(latLng);
    } else {
      markerRef.current = new window.google.maps.Marker({
        position: latLng,
        map,
        draggable: true,
        icon: buildPinIcon(),
        zIndex: 999,
      });
      markerRef.current.addListener("dragend", (e) => {
        setPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      });
    }
  }, []);

  // ── Boundary polygon helpers (manual drawing) ──────────────────────────────
  const updateBoundaryFromPolygon = useCallback((polygon) => {
    const path = polygon
      .getPath()
      .getArray()
      .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));
    setBoundaryPath(path);
  }, []);

  const attachPolygonEditListeners = useCallback(
    (polygon) => {
      const path = polygon.getPath();
      window.google.maps.event.addListener(path, "set_at", () => updateBoundaryFromPolygon(polygon));
      window.google.maps.event.addListener(path, "insert_at", () => updateBoundaryFromPolygon(polygon));
      window.google.maps.event.addListener(path, "remove_at", () => updateBoundaryFromPolygon(polygon));
    },
    [updateBoundaryFromPolygon]
  );

  const drawExistingPolygon = useCallback(
    (points) => {
      const map = mapInstanceRef.current;
      if (!map || !window.google || !points?.length) return;
      polygonRef.current?.setMap(null);
      const polygon = new window.google.maps.Polygon({
        paths: points,
        map,
        editable: true,
        draggable: true,
        fillColor: BOUNDARY_COLOR,
        fillOpacity: 0.15,
        strokeColor: BOUNDARY_COLOR,
        strokeWeight: 2,
      });
      polygonRef.current = polygon;
      attachPolygonEditListeners(polygon);
    },
    [attachPolygonEditListeners]
  );

  const clearVertexMarkers = () => {
    vertexMarkersRef.current.forEach((m) => m.setMap(null));
    vertexMarkersRef.current = [];
  };

  const addVertexMarker = (latLng) => {
    const marker = new window.google.maps.Marker({
      position: latLng,
      map: mapInstanceRef.current,
      clickable: false,
      zIndex: 998,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 5,
        fillColor: BOUNDARY_COLOR,
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
      },
    });
    vertexMarkersRef.current.push(marker);
  };

  const drawBoundaryPreview = (points) => {
    const map = mapInstanceRef.current;
    if (!map || !window.google) return;
    if (!tempPolygonRef.current) {
      tempPolygonRef.current = new window.google.maps.Polygon({
        map,
        paths: points,
        clickable: false, // let map clicks pass through so we can keep adding points
        fillColor: BOUNDARY_COLOR,
        fillOpacity: 0.12,
        strokeColor: BOUNDARY_COLOR,
        strokeWeight: 2,
      });
    } else {
      tempPolygonRef.current.setPath(points);
    }
  };

  const startDrawingBoundary = () => {
    // Drop any previously finalized boundary — we're drawing a new one
    polygonRef.current?.setMap(null);
    polygonRef.current = null;
    setBoundaryPath(null);

    drawingPointsRef.current = [];
    setDrawingPointCount(0);
    isDrawingBoundaryRef.current = true;
    setIsDrawingBoundary(true);
  };

  const cancelDrawingBoundary = () => {
    isDrawingBoundaryRef.current = false;
    setIsDrawingBoundary(false);
    drawingPointsRef.current = [];
    setDrawingPointCount(0);
    tempPolygonRef.current?.setMap(null);
    tempPolygonRef.current = null;
    clearVertexMarkers();
  };

  const undoLastPoint = () => {
    if (!drawingPointsRef.current.length) return;
    drawingPointsRef.current = drawingPointsRef.current.slice(0, -1);
    setDrawingPointCount(drawingPointsRef.current.length);
    const last = vertexMarkersRef.current.pop();
    last?.setMap(null);
    drawBoundaryPreview(drawingPointsRef.current);
  };

  const finishDrawingBoundary = () => {
    if (drawingPointsRef.current.length < 3) return;
    const finalPoints = drawingPointsRef.current;

    tempPolygonRef.current?.setMap(null);
    tempPolygonRef.current = null;
    clearVertexMarkers();

    const polygon = new window.google.maps.Polygon({
      paths: finalPoints,
      map: mapInstanceRef.current,
      editable: true,
      draggable: true,
      fillColor: BOUNDARY_COLOR,
      fillOpacity: 0.15,
      strokeColor: BOUNDARY_COLOR,
      strokeWeight: 2,
    });
    polygonRef.current = polygon;
    updateBoundaryFromPolygon(polygon);
    attachPolygonEditListeners(polygon);

    isDrawingBoundaryRef.current = false;
    setIsDrawingBoundary(false);
    drawingPointsRef.current = [];
    setDrawingPointCount(0);
  };

  const clearBoundary = () => {
    polygonRef.current?.setMap(null);
    polygonRef.current = null;
    setBoundaryPath(null);
    if (isDrawingBoundaryRef.current) cancelDrawingBoundary();
  };

  // ── Existing locations reference layer (dots + per-street outlines) ────────
  const clearExistingOverlays = useCallback(() => {
    existingMarkersRef.current.forEach((m) => m.setMap(null));
    existingMarkersRef.current = [];
    existingOverlaysRef.current.forEach((o) => o.setMap(null));
    existingOverlaysRef.current = [];
    existingInfoWindowRef.current?.close();
  }, []);

  const renderExistingOverlays = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map || !window.google) return;

    clearExistingOverlays();
    if (!showExistingLayer) return;

    if (!existingInfoWindowRef.current) {
      existingInfoWindowRef.current = new window.google.maps.InfoWindow();
    }

    // Small reference dot for every existing point (color by status)
    existingGroups.forEach((group) => {
      group.points.forEach((pt) => {
        const marker = new window.google.maps.Marker({
          position: { lat: pt.lat, lng: pt.lng },
          map,
          clickable: true,
          zIndex: 500,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 5,
            fillColor:
              pt.device_status === "ACTIVE" ? EXISTING_ACTIVE_COLOR : EXISTING_INACTIVE_COLOR,
            fillOpacity: 0.9,
            strokeColor: "#ffffff",
            strokeWeight: 1.5,
          },
        });
        marker.addListener("click", () => {
          existingInfoWindowRef.current.setContent(
            `<div style="font-size:12px;line-height:1.4;">
              <strong>PND ${pt.pnd_number ?? "—"}</strong><br/>
              ${pt.street_name || pt.street_cd || "—"}<br/>
              ${pt.device_type || "—"} • ${pt.device_status || "—"}
            </div>`
          );
          existingInfoWindowRef.current.open(map, marker);
        });
        existingMarkersRef.current.push(marker);
      });

      // Outline the group's footprint: a hull polygon for 3+ points, or a
      // simple dashed line between 2 points. Single-point groups just get
      // the dot above — nothing to outline.
      if (group.points.length >= 3) {
        const hull = convexHull(group.points);
        if (hull) {
          const polygon = new window.google.maps.Polygon({
            paths: hull,
            map,
            clickable: false,
            fillColor: GROUP_OUTLINE_COLOR,
            fillOpacity: 0.08,
            strokeColor: GROUP_OUTLINE_COLOR,
            strokeWeight: 1.5,
            strokeOpacity: 0.9,
            zIndex: 400,
          });
          existingOverlaysRef.current.push(polygon);
        }
      } else if (group.points.length === 2) {
        const polyline = new window.google.maps.Polyline({
          path: group.points.map((p) => ({ lat: p.lat, lng: p.lng })),
          map,
          clickable: false,
          strokeColor: GROUP_OUTLINE_COLOR,
          strokeOpacity: 0.9,
          strokeWeight: 1.5,
          icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3 }, offset: "0", repeat: "10px" }],
          zIndex: 400,
        });
        existingOverlaysRef.current.push(polyline);
      }
    });
  }, [existingGroups, showExistingLayer, clearExistingOverlays]);

  // ── Init map once ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady || !mapRef.current || mapInstanceRef.current) return;

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: position || DEFAULT_CENTER,
      zoom: position ? PIN_ZOOM : DEFAULT_ZOOM,
      styles: isDarkMode ? getDarkMapStyles() : getLightMapStyles(),
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
    });

    mapInstanceRef.current.addListener("click", (e) => {
      const latLng = { lat: e.latLng.lat(), lng: e.latLng.lng() };

      if (isDrawingBoundaryRef.current) {
        drawingPointsRef.current = [...drawingPointsRef.current, latLng];
        setDrawingPointCount(drawingPointsRef.current.length);
        addVertexMarker(latLng);
        drawBoundaryPreview(drawingPointsRef.current);
        return;
      }

      setPosition(latLng);
      placeMarker(latLng);
    });

    if (position) placeMarker(position);
    if (initialBoundary) drawExistingPolygon(initialBoundary);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady]);

  // ── Keep map theme in sync ─────────────────────────────────────────────────
  useEffect(() => {
    mapInstanceRef.current?.setOptions({
      styles: isDarkMode ? getDarkMapStyles() : getLightMapStyles(),
    });
  }, [isDarkMode]);

  // ── Draw the existing-locations reference layer once the map instance
  // exists, and re-draw whenever the data, toggle, or theme changes ─────────
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) return;
    renderExistingOverlays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, existingGroups, showExistingLayer, isDarkMode]);

  // ── Auto-fit the viewport to the loaded data ────────────────────────────────
  // Without this the map sits at the DEFAULT_ZOOM world view, and a whole
  // street's worth of points (spanning maybe a few hundred metres) all
  // collapse into a single sub-pixel dot — the hull "polygon" is technically
  // there but invisible. Zoom/pan once, as soon as data is ready, so the
  // points + street outlines are actually visible.
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || loadingExisting || hasAutoFitRef.current) return;

    const map = mapInstanceRef.current;
    const allPoints = [];
    if (position && !isNaN(parseFloat(position.lat)) && !isNaN(parseFloat(position.lng))) {
      allPoints.push({ lat: parseFloat(position.lat), lng: parseFloat(position.lng) });
    }
    filteredExisting.forEach((r) => {
      const lat = parseFloat(r.latitude);
      const lng = parseFloat(r.longitude);
      if (!isNaN(lat) && !isNaN(lng)) allPoints.push({ lat, lng });
    });

    if (!allPoints.length) return; // nothing to fit to — leave default view as-is
    hasAutoFitRef.current = true;

    if (allPoints.length === 1) {
      map.setCenter(allPoints[0]);
      map.setZoom(PIN_ZOOM);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    allPoints.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 64);
    // fitBounds can over-zoom for a very tight cluster of points — cap it
    window.google.maps.event.addListenerOnce(map, "bounds_changed", () => {
      if (map.getZoom() > 19) map.setZoom(19);
    });
  }, [mapReady, loadingExisting, filteredExisting, position]);

  // ── Cleanup existing-layer overlays on unmount ──────────────────────────────
  useEffect(() => {
    return () => clearExistingOverlays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Manual lat/lng inputs (in case user wants to type instead of clicking) ─
  const handleCoordInputChange = (key, value) => {
    setPosition((prev) => {
      const next = { lat: prev?.lat ?? "", lng: prev?.lng ?? "", [key]: value };
      const lat = parseFloat(next.lat);
      const lng = parseFloat(next.lng);
      if (!isNaN(lat) && !isNaN(lng)) {
        placeMarker({ lat, lng });
        mapInstanceRef.current?.panTo({ lat, lng });
      }
      return next;
    });
  };

  const isValidPosition =
    position &&
    position.lat !== "" &&
    position.lng !== "" &&
    !isNaN(parseFloat(position.lat)) &&
    !isNaN(parseFloat(position.lng));

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!isValidPosition) return;
    try {
      setSaving(true);
      const payload = {
        latitude: parseFloat(position.lat),
        longitude: parseFloat(position.lng),
        ...(boundaryPath ? { boundary: boundaryPath } : {}),
      };
      if (mode === "update" && pndNumber) {
        // TODO: confirm the real update-location endpoint with backend
        await apiCall("POST", `/cp-map/pnd-street/update-location/${pndNumber}`, payload, navigate);
      } else {
        // TODO: confirm the real add-location endpoint with backend
        await apiCall("POST", `/cp-map/pnd-street/add-location`, payload, navigate);
      }
      navigate("/on-street-master-data");
    } catch (err) {
      console.error("Failed to save location:", err);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="relative h-screen w-full overflow-hidden bg-background">
      {/* ── Top bar ── */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center gap-3 px-4 py-3 bg-card/95 backdrop-blur border-b border-border">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          title="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="min-w-0">
          <h1 className="text-sm font-semibold text-foreground">
            {mode === "update" ? "Update Device Location" : "Add Device Location"}
          </h1>
          <p className="text-xs text-muted-foreground truncate">
            {isDrawingBoundary
              ? `Click to add boundary points (${drawingPointCount} added) — need at least 3 to finish`
              : mode === "update"
              ? `Click on the map or drag the pin to update coordinates${pndNumber ? ` for PND ${pndNumber}` : ""}`
              : "Click anywhere on the map to place a pin for the new location"}
          </p>
        </div>
      </div>

      {/* ── Existing locations layer toggle + legend ── */}
      <div className="absolute top-[76px] right-4 z-30 bg-card border border-border rounded-lg shadow-lg px-3 py-2 w-56">
        <button
          type="button"
          onClick={() => setShowExistingLayer((p) => !p)}
          className="w-full flex items-center justify-between gap-2 text-xs font-medium text-foreground"
        >
          <span className="flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5 text-primary" />
            Existing Locations
          </span>
          <span
            className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
              showExistingLayer ? "bg-primary" : "bg-secondary"
            }`}
          >
            <span
              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                showExistingLayer ? "translate-x-3.5" : "translate-x-0.5"
              }`}
            />
          </span>
        </button>
        <p className="text-[11px] text-muted-foreground mt-1">
          {loadingExisting
            ? "Loading existing points…"
            : `${filteredExisting.length} point${filteredExisting.length === 1 ? "" : "s"}${
                contextParkCd || contextCityCd ? " (this project)" : ""
              }`}
        </p>
        {showExistingLayer && !loadingExisting && filteredExisting.length > 0 && (
          <div className="flex flex-col gap-1 mt-2 pt-2 border-t border-border">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: EXISTING_ACTIVE_COLOR }} />
              Active device
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: EXISTING_INACTIVE_COLOR }} />
              Inactive device
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span
                className="w-2.5 h-2.5 rounded-sm border"
                style={{ borderColor: GROUP_OUTLINE_COLOR, backgroundColor: `${GROUP_OUTLINE_COLOR}22` }}
              />
              Street outline
            </div>
          </div>
        )}
      </div>

      {/* ── Map ── */}
      <div ref={mapRef} className="h-full w-full pt-[64px]" />

      {!mapReady && !mapLoadError && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {mapLoadError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background z-20 px-4 text-center">
          <p className="text-sm font-medium text-foreground">Map could not be loaded</p>
          <p className="text-xs text-muted-foreground max-w-xs">
            Check your internet connection or the Google Maps API key configuration, then try again.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 h-8 px-3 rounded-md border border-border text-xs text-foreground hover:bg-secondary transition-colors"
          >
            Reload
          </button>
        </div>
      )}

      {/* ── While actively drawing a boundary: floating Finish/Undo/Cancel toolbar ── */}
      {isDrawingBoundary && (
        <div className="absolute top-[76px] left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-card border border-border rounded-lg shadow-lg px-2 py-2">
          <button
            type="button"
            onClick={undoLastPoint}
            disabled={!drawingPointCount}
            title="Undo last point"
            className="h-8 px-2.5 rounded-md text-xs font-medium flex items-center gap-1.5 text-foreground hover:bg-secondary transition-colors disabled:opacity-40"
          >
            <Undo2 className="w-3.5 h-3.5" /> Undo
          </button>
          <button
            type="button"
            onClick={cancelDrawingBoundary}
            title="Cancel drawing"
            className="h-8 px-2.5 rounded-md text-xs font-medium flex items-center gap-1.5 text-foreground hover:bg-secondary transition-colors"
          >
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
          <button
            type="button"
            onClick={finishDrawingBoundary}
            disabled={drawingPointCount < 3}
            title="Finish boundary"
            className="h-8 px-2.5 rounded-md text-xs font-medium flex items-center gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-40"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Finish
          </button>
        </div>
      )}

      {/* ── Coordinates + Save panel ── */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 w-[95vw] max-w-md bg-card border border-border rounded-xl shadow-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium text-foreground">Coordinates</span>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs text-muted-foreground">Latitude</label>
            <input
              type="number"
              step="any"
              value={position?.lat ?? ""}
              onChange={(e) => handleCoordInputChange("lat", e.target.value)}
              placeholder="e.g. 24.7136"
              className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-background text-sm text-foreground"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Longitude</label>
            <input
              type="number"
              step="any"
              value={position?.lng ?? ""}
              onChange={(e) => handleCoordInputChange("lng", e.target.value)}
              placeholder="e.g. 46.6753"
              className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-background text-sm text-foreground"
            />
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Shapes className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-foreground">
              Zone Boundary <span className="text-muted-foreground font-normal">(optional)</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            {boundaryPath && !isDrawingBoundary && (
              <button
                type="button"
                onClick={clearBoundary}
                title="Clear boundary"
                className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={startDrawingBoundary}
              disabled={isDrawingBoundary}
              className="h-7 px-2.5 rounded-md text-xs font-medium flex items-center gap-1.5 border border-border text-foreground hover:bg-secondary transition-colors disabled:opacity-50"
            >
              <PenLine className="w-3.5 h-3.5" />
              {isDrawingBoundary ? "Drawing…" : boundaryPath ? "Redraw" : "Draw Boundary"}
            </button>
          </div>
        </div>
        {boundaryPath && !isDrawingBoundary && (
          <p className="text-xs text-muted-foreground -mt-2 mb-3">
            {boundaryPath.length} points • drag a corner to adjust
          </p>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 h-9 rounded-md border border-border text-sm text-foreground hover:bg-secondary transition-colors flex items-center justify-center gap-1.5"
          >
            <X className="w-4 h-4" /> Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !isValidPosition || isDrawingBoundary}
            className="flex-1 h-9 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {saving ? "Saving..." : mode === "update" ? "Update Location" : "Save Location"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationPickerPage;